// A product category's traits: ONE table over the categories the app knows, a
// field per decision a category makes, each named after what it decides. A
// category the table lacks (a free-form one a user typed in the catalogue) is a
// plain part (`PLAIN`), by the one rule in `traitsOf`. Pure, so the app, the
// desktop store and the server share it (docs/claude/products.md, *A
// category's traits*; test/categories.test.mjs holds the whole table).

import type {
  AppCategory,
  Discipline,
} from './types';

/** The catalogue's free-form categories the app knows by name: one per steam
 *  generator make, and the one they all had before (copies in older drawings
 *  carry it). They are not offered by the product editor's list. */
export type CatalogueCategory = 'Steam Generators' | 'Steam generators Carel' | 'Steam generators Omega' | 'Steam generators Zephyr';

export type KnownCategory = AppCategory | CatalogueCategory;

/** A body the app builds from a part's own figures (renderer/partLook.ts). */
export type BuiltBody =
  | 'conveyor'
  | 'doser'
  | 'fan'
  | 'hotTub'
  | 'steamCube';

/** A category's own symbol on the plan (render.ts). */
export type CategorySymbol =
  | 'canalina'
  | 'drain'
  | 'heatedBench'
  | 'hotTub'
  | 'infrared'
  | 'lighting'
  | 'saltWall'
  | 'saunaBench'
  | 'steamCube';

/** The catalogue's headings, in the list's order, numbered as Stenal's price
 *  list (set by the user). Each has its it / fr entry, the number kept. */
export const CATALOGUE_GROUPS = [
  '01 Saunas',
  '01.1 Sauna optionals',
  '01.7 Sauna heaters',
  '01.8 Sauna Control',
  '02 Steam Rooms',
  '02.1 Steam room optionals',
  '02.6 Steam generators Carel',
  '02.7 Steam generators Omega',
  '02.8 Steam generators Zephyr',
  '03 Showers',
  '06 Relax',
  '07 Salt',
  '09.5 Hot Tubs',
  '16 SpaManager',
  '95 Plumbing',
  '96 Lighting',
  'Custom',
] as const;
export type CatalogueGroup = (typeof CATALOGUE_GROUPS)[number];

export interface CategoryTraits {
  /** One object's category, as its panel badge and the product editor's list
   *  name it (an English DICT key, shown through tr()); null: named by its own
   *  value, and not offered by the editor's list. */
  name: string | null;
  /** The colour of its badge, its Parts tab row and a new product's swatch. */
  color: string;
  /** The catalogue heading it is listed under; null: a heading of its own,
   *  named by its value, after the listed ones. */
  group: CatalogueGroup | null;
  /** The discipline whose toggle shows and hides its placed copies. */
  discipline: Discipline | null;
  /** A room: drawn as its walls, its benches laid out for it. */
  room: boolean;
  /** Placed along a drawn run of nodes, its Height set by a handle on the run. */
  run: boolean;
  /** Placed, dragged and turned as a sauna heater: dropped in a sauna it turns
   *  with it and snaps to its walls, turns square to the wall it nears, and
   *  stays put in the room when the room turns. */
  placedAsHeater: boolean;
  /** Has a clearance gap and a guard cover (`heaterGuard`): drawn, carved,
   *  snapped and edited with them; the section's heater blocks. */
  guarded: boolean;
  /** Drawn as a parametric heater block when it has no drawing of its own
   *  (`drawsHeaterBlock`). */
  heaterBlock: boolean;
  /** Its body floats by its Height from ground and stands its own Height tall,
   *  else its product's body height, else 750 (`partSpan`); its lift handle. */
  floatsBody: boolean;
  /** Its copies start 150 above the floor when the catalogue gives no Height
   *  from ground, and the old field name is still written for older builds. */
  liftedByDefault: boolean;
  /** The catalogue field its height is kept in (a copy's is `height` either way). */
  heightKey: 'heaterHeight' | 'height';
  /** The height of a copy with none of its own; 'model': its hot tub model's. */
  defaultHeight: number | 'model';
  /** Its footprint carves a hole in the benches of the room it stands in. */
  carvesBenches: boolean;
  /** May turn on its own X and Y axes (`canTilt`). */
  tilts: boolean;
  /** Tilted while against a wall, it is kept touching that wall. */
  tiltsAgainstWall: boolean;
  /** Drawn by the app, not from its drawing: the drawing editor never turns its
   *  geometry into shapes. */
  procedural: boolean;
  /** The body the 3D view builds and a section cuts from the part's own figures,
   *  when they fit its footprint (renderer/partLook.ts); null: its box. */
  body: BuiltBody | null;
  /** Its own symbol on the plan, in place of its box (render.ts; partLook.ts
   *  says when it gives way); null: none. */
  symbol: CategorySymbol | null;
  /** Its place in a cell of the electrical schematic, lowest first; null:
   *  after every ranked one, by category. */
  elecRank: number | null;
}

/** A plain part: what a free-form category is, and what each entry starts from. */
export const PLAIN: Readonly<CategoryTraits> = {
  name: null,
  color: '#cdd6e0',
  group: null,
  discipline: null,
  room: false,
  run: false,
  placedAsHeater: false,
  guarded: false,
  heaterBlock: false,
  floatsBody: false,
  liftedByDefault: false,
  heightKey: 'height',
  defaultHeight: 1000,
  carvesBenches: false,
  tilts: false,
  tiltsAgainstWall: false,
  procedural: false,
  body: null,
  symbol: null,
  elecRank: null,
};

const ROOM = { ...PLAIN, room: true };
const RUN = { ...PLAIN, run: true, procedural: true };
const STEAM_GENERATOR = { ...PLAIN, tilts: true };

/** Every known category's traits, in the product editor's order. */
const TRAITS: { readonly [C in KnownCategory]: Readonly<CategoryTraits> } = {
  sauna: { ...ROOM, name: 'Sauna', color: '#c8853c', group: '01 Saunas' },
  steamroom: { ...ROOM, name: 'Steam room', color: '#3c8fc8', group: '02 Steam Rooms' },
  shower: {
    ...PLAIN, name: 'Shower', color: '#3cc89a', group: '03 Showers',
    heaterBlock: true, floatsBody: true, liftedByDefault: true, heightKey: 'heaterHeight', tilts: true, elecRank: 90,
  },
  heater: {
    ...PLAIN, name: 'Sauna heater', color: '#4b4b4b', group: '01.7 Sauna heaters',
    placedAsHeater: true, guarded: true, heaterBlock: true, floatsBody: true, liftedByDefault: true, heightKey: 'heaterHeight', carvesBenches: true, elecRank: 20,
  },
  infrared: {
    ...PLAIN, name: 'Infrared emitter', color: '#8b1a1a', group: '01.1 Sauna optionals',
    liftedByDefault: true, tilts: true, tiltsAgainstWall: true, symbol: 'infrared', elecRank: 40,
  },
  steamcube: { ...PLAIN, name: 'Steam cube', color: '#3c3d42', group: '02.1 Steam room optionals', defaultHeight: 600, carvesBenches: true, procedural: true, body: 'steamCube', symbol: 'steamCube', elecRank: 50 },
  // A conveyor to its copper pipe's top, a doser to its back panel's.
  steamconveyor: { ...PLAIN, name: 'Steam conveyor', color: '#b8bec4', group: '02.1 Steam room optionals', defaultHeight: 225, tilts: true, body: 'conveyor', elecRank: 60 },
  essencedoser: { ...PLAIN, name: 'Essence doser', color: '#1f1f1f', group: '02.1 Steam room optionals', defaultHeight: 630, body: 'doser', elecRank: 110 },
  hottub: { ...PLAIN, name: 'Hot tub', color: '#3fb6d6', group: '09.5 Hot Tubs', defaultHeight: 'model', procedural: true, body: 'hotTub', symbol: 'hotTub', elecRank: 100 },
  saltwall: { ...RUN, name: 'Salt wall', color: '#f6c4d2', group: '07 Salt', defaultHeight: 2000, symbol: 'saltWall', elecRank: 80 },
  heatedbench: { ...RUN, name: 'Heated bench', color: '#c8955a', group: '06 Relax', defaultHeight: 500, symbol: 'heatedBench', elecRank: 70 },
  saunabench: { ...RUN, name: 'Sauna bench', color: '#c9a36a', group: '06 Relax', defaultHeight: 500, symbol: 'saunaBench' },
  repeatable: { ...PLAIN, name: 'Repeatable', color: '#e6d9b8', group: '06 Relax' },
  // Drains and drain channels lie flat on the floor; a duct fan with no height
  // of its own (a hand-made product) is 200 tall.
  canalina: { ...PLAIN, name: 'Drain channel', color: '#ffff00', group: '95 Plumbing', discipline: 'plumbing', defaultHeight: 0, procedural: true, symbol: 'canalina' },
  drain: { ...PLAIN, name: 'Drain', color: '#ffff00', group: '95 Plumbing', discipline: 'plumbing', defaultHeight: 0, procedural: true, symbol: 'drain' },
  // A shower mixer set into a wall: its plate, knob and hand shower out of it.
  mixer: { ...PLAIN, name: 'Shower mixer', color: '#c9ccd0', group: '95 Plumbing', discipline: 'plumbing', defaultHeight: 392 },
  fan: { ...PLAIN, name: 'Fan', color: '#c9d1d8', group: '95 Plumbing', discipline: 'plumbing', defaultHeight: 200, tilts: true, body: 'fan', elecRank: 120 },
  // Only the fibre projector is placed as a part; strips and spots are drawn
  // with the Lighting tools, and the Lighting toggle hides them all.
  lighting: { ...PLAIN, name: 'Lighting', color: '#ffb020', group: '96 Lighting', discipline: 'lighting', tilts: true, symbol: 'lighting', elecRank: 10 },
  // The Spa Manager and what it drives (junction boxes …): the schematic's.
  spamanager: { ...PLAIN, name: 'SpaManager', color: '#9aa0a6', group: '16 SpaManager', tilts: true, elecRank: 130 },
  // A sauna's controls: a display set into a wall, its motherboard, its sensor.
  controlunit: { ...PLAIN, name: 'Control unit', color: '#2b2d30', group: '01.8 Sauna Control', tilts: true, elecRank: 30 },
  custom: { ...PLAIN, name: 'Custom', color: '#b07cff', group: 'Custom', tilts: true },
  'Steam Generators': STEAM_GENERATOR,
  'Steam generators Carel': { ...STEAM_GENERATOR, group: '02.6 Steam generators Carel' },
  'Steam generators Omega': { ...STEAM_GENERATOR, group: '02.7 Steam generators Omega' },
  'Steam generators Zephyr': { ...STEAM_GENERATOR, group: '02.8 Steam generators Zephyr' },
};

/** Every known category, in the product editor's order. */
export const KNOWN_CATEGORIES = Object.keys(TRAITS) as KnownCategory[];

/** A category's traits: a known one's entry, any other (a free-form category,
 *  none at all) a plain part's. */
export function traitsOf(category: string | null | undefined): Readonly<CategoryTraits> {
  return typeof category === 'string' && Object.prototype.hasOwnProperty.call(TRAITS, category) ? TRAITS[category as KnownCategory] : PLAIN;
}
